/**
 * Le carnet de commandes daté : combien on facture, quel mois, et de combien on glisse.
 *
 * *Demande de Chris, 21/09/2026 : « un onglet Facturation Prévisionnelle […] l'idée étant
 * d'avoir en permanence le montant facturable à partir des copies de travail figées puis des
 * copies de CE modifiables, en pouvant distinguer par APO puis par chapitre ce qui était prévu
 * de ce qui est en train de se passer », puis ses arbitrages : « présentation mois en colonnes
 * avec le total car le but est de voir aussi la totalité du carnet de commande », « ok pour le
 * mois en cours à +6 mois », « ok pour la présentation non daté et non planifié pour avoir les
 * chiffres, sinon dans le comparatif on ne compare que ce qui est validé et donc en CE », et
 * « il faudra pouvoir analyser combien on prend de retard de CA en permanence ».*
 *
 * ## Les montants sont ceux du Gantt, posés par le moteur
 *
 * *« On est bien d'accord que tu positionnes les facturations par rapport à ce que nous avons
 * dit pour les lignes de chapitre ? »* — oui. Le serveur appelle `echeancesDeLaVersion`, la
 * fonction qui pose déjà le CA au bout de la barre grise : même montant, même dernier jour
 * occupé, pastilles comprises, et le devis plat porte ses montants sur ses tâches. Cet écran ne
 * calcule **aucun** euro et **aucune** date — il range.
 *
 * ## Trois colonnes qui ne sont pas des mois, et elles sont indispensables
 *
 * `NON DATÉ` (le chantier n'a pas de date de démarrage), `NON PLANIFIÉ` (le chapitre ne porte ni
 * barre ni pastille) et `HORS HORIZON` (le mois tombe avant ou après les bornes). Sans elles, la
 * ligne TOTAL n'égalerait pas le carnet et rien ne dirait où est passée la différence : sur la
 * production du 21/09/2026, les chantiers sans date de démarrage pèsent **65 % du carnet**.
 *
 * ## Le retard de CA, et pourquoi il s'accumule tout seul
 *
 * Le prévu vient de la copie **figée** à la validation, qui ne bougera plus ; l'en cours de la CE
 * **modifiable**. L'écart entre leurs mois est donc, par construction, tout ce que le chantier a
 * pris de retard depuis qu'il a été validé — sans qu'on ait à photographier quoi que ce soit.
 * C'est la réponse à « en permanence ».
 *
 * Il ne se calcule que sur les chantiers **comparables**. Les autres n'ont jamais été validés :
 * ils n'ont rien promis, ils ne peuvent pas être en retard.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { formaterDate, formaterMoisCourt, formaterMontant, moisEntre } from "@planif/moteur";
import { chargerFacturation } from "./api";
import type { CarnetDeFacturation, ChantierFacture, LigneFacturation } from "./api";

/** Les trois colonnes qui ne sont pas des mois, dans leur ordre d'affichage. */
const HORS_MOIS = ["hors_horizon", "non_date", "non_planifie"] as const;
type HorsMois = (typeof HORS_MOIS)[number];

const LIBELLE_HORS_MOIS: Record<HorsMois, string> = {
  hors_horizon: "Hors horizon",
  non_date: "Non daté",
  non_planifie: "Non planifié",
};

const EXPLICATION_HORS_MOIS: Record<HorsMois, string> = {
  hors_horizon: "Le chapitre se facture avant ou après les bornes affichées. Élargissez l'horizon pour le voir tomber dans son mois.",
  non_date: "Le chantier n'a pas de date de démarrage : son planning est en jours relatifs, aucun jour ne se convertit en mois.",
  non_planifie: "Le chapitre ne porte aucune barre ni aucune pastille : rien ne dit encore quand il se termine.",
};

/** Quelle colonne reçoit une échéance, une fois l'horizon connu. */
export function colonneDe(ligne: LigneFacturation, mois: readonly string[]): string {
  if (ligne.sansMois === "sans_date_de_demarrage") return "non_date";
  if (ligne.sansMois === "non_planifie") return "non_planifie";
  if (ligne.mois === null) return "non_planifie";
  return mois.includes(ligne.mois) ? ligne.mois : "hors_horizon";
}

/** Les totaux d'une série d'échéances, par colonne, plus le total général. */
export function ventiler(
  lignes: readonly LigneFacturation[],
  mois: readonly string[],
): { readonly parColonne: ReadonlyMap<string, number>; readonly total: number } {
  const parColonne = new Map<string, number>();
  let total = 0;
  for (const ligne of lignes) {
    const colonne = colonneDe(ligne, mois);
    const montant = Number(ligne.montant);
    parColonne.set(colonne, (parColonne.get(colonne) ?? 0) + montant);
    total += montant;
  }
  return { parColonne, total };
}

export type RetardDeCA = {
  /** Ce qui part vers un mois plus tard. */
  readonly repousse: number;
  /** Ce qui revient vers un mois plus tôt. */
  readonly avance: number;
  /** Le décalage en mois le plus lourd rencontré, et le poste qui le porte. */
  readonly pire: { readonly apo: string; readonly numero: string; readonly mois: number } | null;
  /** Des chapitres dont on ne peut rien dire : datés d'un côté, pas de l'autre. */
  readonly indecidables: number;
};

/**
 * Ce que le carnet a pris de retard depuis les validations.
 *
 * **Seuls les chantiers comparables comptent** — décision de Chris : *« dans le comparatif on
 * ne compare que ce qui est validé et donc en CE »*. Un chantier jamais soumis n'a rien promis.
 *
 * Un chapitre daté d'un seul côté n'est ni un retard ni une avance : le compter à zéro
 * dissoudrait dans la masse le fait qu'on ne sait pas. Il est compté à part, et l'écran le dit.
 */
export function retardDeCA(chantiers: readonly ChantierFacture[]): RetardDeCA {
  let repousse = 0;
  let avance = 0;
  let indecidables = 0;
  let pire: RetardDeCA["pire"] = null;
  for (const chantier of chantiers) {
    if (!chantier.comparable) continue;
    // Apparié par poste ET par période : une tâche hachée a une échéance par période
    // (25/09/2026). Si le découpage a changé depuis la validation — trois périodes prévues,
    // deux en cours —, la « même » période n'existe plus : indécidable, et compté comme tel.
    const cle = (l: LigneFacturation): string => `${l.numero}#${String(l.rang)}/${String(l.parts)}`;
    const enCours = new Map(chantier.enCours.map((l) => [cle(l), l]));
    for (const prevu of chantier.prevu) {
      const suite = enCours.get(cle(prevu));
      if (suite === undefined) {
        indecidables += 1;
        continue;
      }
      if (prevu.mois === null || suite.mois === null) {
        indecidables += 1;
        continue;
      }
      const decalage = moisEntre(prevu.mois, suite.mois);
      if (decalage > 0) {
        repousse += Number(suite.montant);
        if (pire === null || decalage > pire.mois) {
          pire = { apo: chantier.apo, numero: prevu.numero, mois: decalage };
        }
      } else if (decalage < 0) {
        avance += Number(suite.montant);
      }
    }
  }
  return { repousse, avance, pire, indecidables };
}

type Source = "les_deux" | "prevu" | "en_cours";

const LIBELLE_SOURCE: Record<Source, string> = {
  les_deux: "Les deux",
  prevu: "Prévu seul",
  en_cours: "En cours seul",
};

/** Une cellule de montant : vide quand il n'y a rien, jamais « 0,00 € ». */
function Montant({ valeur }: { readonly valeur: number | undefined }): JSX.Element {
  // Un zéro écrit partout ferait un tableau de zéros où l'œil ne trouve plus les montants.
  // Le tiret dit « rien ici », ce que « 0,00 € » ne dit pas — il dit « exactement zéro euro ».
  if (valeur === undefined || valeur === 0) return <span className="neutre">—</span>;
  return <>{formaterMontant(String(valeur))} €</>;
}

export function FacturationPrevisionnelle(): JSX.Element {
  const [carnet, setCarnet] = useState<CarnetDeFacturation | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [source, setSource] = useState<Source>("les_deux");
  const [bornes, setBornes] = useState<{ debut: string; fin: string } | null>(null);
  const [ouverts, setOuverts] = useState<ReadonlySet<string>>(new Set());
  /** Les chapitres dépliés dans le détail d'une affaire, en `apo#chapitre` (25/09/2026). */
  const [chapitresOuverts, setChapitresOuverts] = useState<ReadonlySet<string>>(new Set());

  const recharger = useCallback((demandees: { debut: string; fin: string } | null) => {
    setChargement(true);
    setErreur(null);
    chargerFacturation(demandees ?? undefined)
      .then((rendu) => {
        setCarnet(rendu);
        // Les bornes affichées sont celles que le SERVEUR a retenues, jamais celles qu'on
        // croyait demander : c'est lui qui sait quel mois on est.
        const premier = rendu.mois[0];
        const dernier = rendu.mois[rendu.mois.length - 1];
        if (premier !== undefined && dernier !== undefined) {
          setBornes({ debut: premier, fin: dernier });
        }
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : "Le carnet n'a pas pu être chargé.");
      })
      .finally(() => {
        setChargement(false);
      });
  }, []);

  useEffect(() => {
    recharger(null);
  }, [recharger]);

  if (chargement && carnet === null) return <p>Chargement du carnet…</p>;
  if (erreur !== null) return <p className="alerte">{erreur}</p>;
  if (carnet === null) return <p className="alerte">Le carnet n'a pas pu être chargé.</p>;

  const mois = carnet.mois;
  const colonnes = [...mois, ...HORS_MOIS];
  const retard = retardDeCA(carnet.chantiers);
  const moisEnCours = carnet.aujourdhui.slice(0, 7);

  /** Les deux séries d'un chantier, dans l'ordre d'affichage, filtrées par la source choisie. */
  const seriesDe = (
    chantier: ChantierFacture,
  ): { readonly cle: "prevu" | "enCours"; readonly libelle: string; readonly lignes: readonly LigneFacturation[] }[] => {
    const series: { cle: "prevu" | "enCours"; libelle: string; lignes: readonly LigneFacturation[] }[] = [];
    if (source !== "en_cours" && chantier.comparable) {
      series.push({ cle: "prevu", libelle: "prévu", lignes: chantier.prevu });
    }
    if (source !== "prevu") {
      series.push({ cle: "enCours", libelle: "en cours", lignes: chantier.enCours });
    }
    return series;
  };

  /** Le total de toutes les affaires, série par série. */
  const totauxGeneraux = (["prevu", "enCours"] as const).map((cle) => {
    const lignes = carnet.chantiers.flatMap((c) =>
      cle === "prevu" ? (c.comparable ? c.prevu : []) : c.enCours,
    );
    return { cle, ...ventiler(lignes, mois) };
  });

  return (
    <section className="facturation">
      <p className="note">
        Le carnet de commandes daté. Chaque montant est <strong>celui du planning</strong> — la
        vente des postes retenus de chaque sous-chapitre (ou tâche posée directement sous un
        chapitre), au dernier jour qu'il occupe, pastilles comprises. Rien n'est recalculé ici.
      </p>

      <div className="facturation__commandes">
        <label>
          Du mois
          <input
            type="month"
            value={bornes?.debut ?? ""}
            onChange={(e) => {
              setBornes((b) => (b === null ? b : { ...b, debut: e.target.value }));
            }}
          />
        </label>
        <label>
          au mois
          <input
            type="month"
            value={bornes?.fin ?? ""}
            onChange={(e) => {
              setBornes((b) => (b === null ? b : { ...b, fin: e.target.value }));
            }}
          />
        </label>
        <button
          type="button"
          disabled={chargement || bornes === null}
          onClick={() => {
            recharger(bornes);
          }}
        >
          Afficher
        </button>
        <button
          type="button"
          className="lien"
          disabled={chargement}
          onClick={() => {
            recharger(null);
          }}
        >
          Revenir au mois en cours → +6
        </button>

        <span className="facturation__sources">
          {(Object.keys(LIBELLE_SOURCE) as Source[]).map((cle) => (
            <button
              key={cle}
              type="button"
              className={source === cle ? "actif" : undefined}
              onClick={() => {
                setSource(cle);
              }}
            >
              {LIBELLE_SOURCE[cle]}
            </button>
          ))}
        </span>
      </div>

      {/*
        **Le retard de CA, en permanence.** C'est la demande la plus tardive de Chris, et la
        raison d'être du couple figé / modifiable : le prévu ne bouge jamais, donc l'écart
        s'accumule sans qu'on ait à photographier quoi que ce soit.
      */}
      <p className={retard.repousse > 0 ? "alerte" : "note"}>
        <strong>Retard de CA</strong> — {formaterMontant(String(retard.repousse))} € repoussés
        vers un mois plus tard, {formaterMontant(String(retard.avance))} € avancés, soit un net de{" "}
        <strong>{formaterMontant(String(retard.repousse - retard.avance))} €</strong>.
        {retard.pire !== null && (
          <>
            {" "}
            Le plus long décalage est de {retard.pire.mois} mois, sur {retard.pire.apo} poste{" "}
            {retard.pire.numero}.
          </>
        )}
        {retard.indecidables > 0 && (
          <>
            {" "}
            <span title="Daté d'un côté et pas de l'autre, ou disparu entre les deux versions : ni un retard ni une avance, et le compter à zéro le dissoudrait dans la masse.">
              {retard.indecidables} poste(s) indécidable(s).
            </span>
          </>
        )}{" "}
        Mesuré sur les seules affaires validées — une affaire jamais soumise n'a rien promis.
      </p>

      <div className="facturation__defilement">
        <table className="facturation__table">
          <thead>
            <tr>
              <th className="facturation__poste">Affaire / poste</th>
              <th className="facturation__serie">Source</th>
              {mois.map((m) => (
                <th
                  key={m}
                  className={`col-nombre${m === moisEnCours ? " facturation__mois-en-cours" : ""}`}
                >
                  {formaterMoisCourt(m)}
                </th>
              ))}
              {HORS_MOIS.map((cle) => (
                <th key={cle} className="col-nombre facturation__hors" title={EXPLICATION_HORS_MOIS[cle]}>
                  {LIBELLE_HORS_MOIS[cle]}
                </th>
              ))}
              <th className="col-nombre facturation__total">Total</th>
            </tr>
          </thead>
          <tbody>
            {/* Le total du carnet en TÊTE : c'est le chiffre qu'on vient chercher. */}
            {totauxGeneraux
              .filter(
                ({ cle }) =>
                  (cle === "prevu" && source !== "en_cours") ||
                  (cle === "enCours" && source !== "prevu"),
              )
              .map(({ cle, parColonne, total }) => (
                <tr key={cle} className="facturation__somme">
                  <th scope="row">TOTAL — {carnet.chantiers.length} affaire(s)</th>
                  <td className="facturation__serie">{cle === "prevu" ? "prévu" : "en cours"}</td>
                  {colonnes.map((c) => (
                    <td key={c} className="col-nombre">
                      <Montant valeur={parColonne.get(c)} />
                    </td>
                  ))}
                  <td className="col-nombre facturation__total">
                    <Montant valeur={total} />
                  </td>
                </tr>
              ))}

            {carnet.chantiers.map((chantier) => {
              const ouvert = ouverts.has(chantier.apo);
              const series = seriesDe(chantier);
              return (
                <Fragment key={chantier.apo}>
                  {series.map(({ cle, libelle, lignes }, rang) => {
                    const { parColonne, total } = ventiler(lignes, mois);
                    return (
                      <tr key={`${chantier.apo}-${cle}`} className="facturation__affaire">
                        {rang === 0 ? (
                          <th scope="row" rowSpan={series.length}>
                            <button
                              type="button"
                              className="lien gantt__plier"
                              title={ouvert ? "Replier le détail" : "Ouvrir le détail par poste"}
                              onClick={() => {
                                setOuverts((courant) => {
                                  const suivant = new Set(courant);
                                  if (!suivant.delete(chantier.apo)) suivant.add(chantier.apo);
                                  return suivant;
                                });
                              }}
                            >
                              {ouvert ? "▾" : "▸"}
                            </button>{" "}
                            <span className="code">{chantier.apo}</span> {chantier.nom}
                            {chantier.dateDebut === null ? (
                              <span className="note" title="Sans date de démarrage, aucune échéance ne se convertit en mois.">
                                {" "}
                                · sans date de démarrage
                              </span>
                            ) : (
                              <span className="note">
                                {" "}
                                · démarrage {formaterDate(chantier.dateDebut)}
                                {chantier.dateDebutStatut !== "ferme" && (
                                  <span
                                    className="en-ecart"
                                    title="Date de démarrage en HYPOTHÈSE : tout le calendrier de ce carnet repose dessus."
                                  >
                                    {" "}
                                    (hypothèse)
                                  </span>
                                )}
                              </span>
                            )}
                            {!chantier.comparable && (
                              <span
                                className="note"
                                title="Jamais soumis à validation : il n'a pas de version figée, donc rien à comparer. Il entre au carnet par sa copie de travail."
                              >
                                {" "}
                                · non validé
                              </span>
                            )}
                          </th>
                        ) : null}
                        <td className="facturation__serie">{libelle}</td>
                        {colonnes.map((c) => (
                          <td key={c} className="col-nombre">
                            <Montant valeur={parColonne.get(c)} />
                          </td>
                        ))}
                        <td className="col-nombre facturation__total">
                          <Montant valeur={total} />
                        </td>
                      </tr>
                    );
                  })}

                  {/*
                    Le détail, REGROUPÉ PAR CHAPITRE (Chris, 25/09/2026 : « montant sur chaque
                    tâche et regroupement par chapitre »). Une affaire porte jusqu'à cent tâches :
                    chaque chapitre arrive replié sur son sous-total, et se déplie sur ses tâches.
                  */}
                  {ouvert &&
                    groupesDuChantier(chantier).map((groupe) => {
                      const cleGroupe = `${chantier.apo}#${groupe.cle}`;
                      const groupeOuvert = chapitresOuverts.has(cleGroupe);
                      return (
                        <Fragment key={cleGroupe}>
                          {series.map(({ cle, libelle, lignes }, rang) => {
                            const { parColonne, total } = ventiler(
                              lignes.filter((l) => groupe.postes.includes(l.numero)),
                              mois,
                            );
                            return (
                              <tr key={`${cleGroupe}-${cle}`} className="facturation__chapitre">
                                {rang === 0 ? (
                                  <th scope="row" rowSpan={series.length} className="facturation__detail">
                                    <button
                                      type="button"
                                      className="lien gantt__plier"
                                      title={groupeOuvert ? "Replier les tâches" : "Ouvrir les tâches du chapitre"}
                                      onClick={() => {
                                        setChapitresOuverts((courant) => {
                                          const suivant = new Set(courant);
                                          if (!suivant.delete(cleGroupe)) suivant.add(cleGroupe);
                                          return suivant;
                                        });
                                      }}
                                    >
                                      {groupeOuvert ? "▾" : "▸"}
                                    </button>{" "}
                                    {groupe.numero !== null && <span className="code">{groupe.numero}</span>}{" "}
                                    {groupe.designation}
                                    <span className="note"> · {groupe.postes.length} tâche(s)</span>
                                  </th>
                                ) : null}
                                <td className="facturation__serie">{libelle}</td>
                                {colonnes.map((c) => (
                                  <td key={c} className="col-nombre">
                                    <Montant valeur={parColonne.get(c)} />
                                  </td>
                                ))}
                                <td className="col-nombre facturation__total">
                                  <Montant valeur={total} />
                                </td>
                              </tr>
                            );
                          })}
                          {groupeOuvert &&
                            groupe.postes.map((numero) =>
                              series.map(({ cle, libelle, lignes }, rang) => {
                                // TOUTES les échéances du poste : une tâche hachée en a une par
                                // période (25/09/2026), et n'en garder qu'une perdrait de l'argent.
                                const { parColonne, total } = ventiler(
                                  lignes.filter((l) => l.numero === numero),
                                  mois,
                                );
                                const designation =
                                  chantier.enCours.find((l) => l.numero === numero)?.designation ??
                                  chantier.prevu.find((l) => l.numero === numero)?.designation ??
                                  "";
                                return (
                                  <tr key={`${cleGroupe}-${numero}-${cle}`} className="facturation__poste-ligne">
                                    {rang === 0 ? (
                                      <th scope="row" rowSpan={series.length} className="facturation__tache">
                                        <span className="code">{numero}</span> {designation}
                                      </th>
                                    ) : null}
                                    <td className="facturation__serie">{libelle}</td>
                                    {colonnes.map((c) => (
                                      <td key={c} className="col-nombre">
                                        <Montant valeur={parColonne.get(c)} />
                                      </td>
                                    ))}
                                    <td className="col-nombre facturation__total">
                                      <Montant valeur={total} />
                                    </td>
                                  </tr>
                                );
                              }),
                            )}
                        </Fragment>
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {carnet.chantiers.length === 0 && (
        <p className="note">
          Aucune affaire dans votre périmètre ne porte de chiffrage : il n'y a pas encore de
          carnet à dater.
        </p>
      )}
    </section>
  );
}

/** Un chapitre du détail d'une affaire : ses tâches, dans l'ordre de l'arbre. */
export type GroupeDuCarnet = {
  /** Le numéro du chapitre, ou la chaîne vide pour les tâches à la racine (devis plat). */
  readonly cle: string;
  readonly numero: string | null;
  readonly designation: string;
  readonly postes: readonly string[];
};

/**
 * Les tâches d'un chantier regroupées par CHAPITRE (Chris, 25/09/2026), les deux versions
 * réunies.
 *
 * Une tâche se range sous le chapitre que lui donne la version en cours, et à défaut la prévue :
 * c'est là que le conducteur la voit aujourd'hui, même si un glisser-déposer l'a déplacée
 * depuis la validation. L'ordre est celui de l'arbre — et non un tri des numéros, qui ne dit
 * plus rien dans une copie restructurée. Une tâche retirée depuis la validation reste listée
 * avec son montant prévu : sans elle, le sous-total du chapitre ne s'expliquerait pas.
 */
export function groupesDuChantier(chantier: ChantierFacture): GroupeDuCarnet[] {
  const groupes = new Map<
    string,
    { cle: string; numero: string | null; designation: string; postes: string[] }
  >();
  const vus = new Set<string>();
  for (const ligne of [...chantier.enCours, ...chantier.prevu]) {
    if (vus.has(ligne.numero)) continue;
    vus.add(ligne.numero);
    const cle = ligne.chapitre?.numero ?? "";
    let groupe = groupes.get(cle);
    if (groupe === undefined) {
      groupe = {
        cle,
        numero: ligne.chapitre?.numero ?? null,
        designation: ligne.chapitre?.designation ?? "Hors chapitre",
        postes: [],
      };
      groupes.set(cle, groupe);
    }
    groupe.postes.push(ligne.numero);
  }
  return [...groupes.values()];
}

